import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/useAuthStore'
import { authService } from '@/api/services/authService'
import { getApiError } from '@/api/client'
import type { UserType } from '@/shared/types'
import { Eye, EyeOff, Shield, School, Mail, X, ArrowLeft, CheckCircle2 } from 'lucide-react'

export function LoginPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const isAdminAccess = searchParams.get('type') === 'admin'

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [userType, setUserType] = useState<UserType>(isAdminAccess ? 'admin' : 'school_admin')
  const [showPwd,  setShowPwd]  = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [forgotOpen, setForgotOpen] = useState(false)

  const setAuth = useAuthStore(s => s.setAuth)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) { setError(t('auth.errorRequired')); return }
    setLoading(true); setError('')

    try {
      const { user, token } = await authService.login({ email, password, user_type: userType })
      setAuth(user, token)
      navigate(user.user_type === 'school_admin' ? '/school/overview' : '/dashboard', { replace: true })
    } catch (err) {
      setError(getApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const Logo = ({ className = 'h-12 w-auto', white = false }: { className?: string; white?: boolean }) => (
    <img
      src="/logo.svg"
      alt="Drivys"
      className={`${className} object-contain`}
      onError={e => {
        const img = e.currentTarget as HTMLImageElement
        img.style.display = 'none'
        const span = document.createElement('span')
        span.style.cssText = `font-size:28px;font-weight:900;letter-spacing:4px;color:${white ? '#fff' : '#f68b2c'}`
        span.textContent = 'DRIVYS'
        img.parentElement?.appendChild(span)
      }}
    />
  )

  return (
    <div className="min-h-screen flex gap-3 lg:gap-5 p-3 lg:p-5" style={{ background: '#ffffff' }}>

      {/* ── Left: black rounded card with white logo (desktop) ── */}
      <div className="hidden lg:flex lg:w-[58%] rounded-[28px] items-center justify-center p-12 relative overflow-hidden shadow-sm"
        style={{ background: '#1c1c1c' }}>
        <img src="/white_letter_logo.png" alt="Drivys"
          className="w-auto max-w-[60%] max-h-[55%] object-contain"
          onError={e => { const i = e.currentTarget as HTMLImageElement; i.style.display='none'
            const s=document.createElement('span'); s.style.cssText='font-size:64px;font-weight:900;letter-spacing:6px;color:#fff'; s.textContent='DRIVYS'; i.parentElement?.appendChild(s) }}/>
      </div>

      {/* ── Right: form card ── */}
      <div className="flex-1 flex items-center justify-center rounded-[28px] shadow-sm p-6 lg:p-10" style={{ background: '#f1f2f4' }}>
        <div className="w-full max-w-[360px]">

          <h1 className="text-2xl font-bold text-gray-900 text-center">{t('auth.welcomeBack')}</h1>
          <p className="text-gray-400 text-xs text-center mt-1 mb-7">
            {isAdminAccess ? t('auth.subtitleAdmin') : t('auth.subtitleSchool')}
          </p>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl border border-red-200 bg-red-50 mb-5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" className="mt-0.5 flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <p className="text-red-600 text-sm leading-snug">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">

            {/* Account type — only shown when arriving via ?type=admin */}
            {isAdminAccess && (
              <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-white border border-gray-100">
                <button type="button" onClick={() => setUserType('admin')}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-[14px] font-semibold transition-all ${
                    userType === 'admin' ? 'bg-brand-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
                  <Shield size={14}/> {t('auth.admin')}
                </button>
                <button type="button" onClick={() => setUserType('school_admin')}
                  className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-[14px] font-semibold transition-all ${
                    userType === 'school_admin' ? 'bg-brand-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
                  <School size={14}/> {t('auth.schoolAdmin')}
                </button>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">{t('auth.emailLabel')}</label>
              <div className="relative">
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  required autoComplete="email" placeholder={t('auth.emailPlaceholder')}
                  className="w-full px-4 py-3 pr-11 rounded-xl bg-white text-gray-900 text-sm placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-all"
                />
                <Mail size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">{t('auth.passwordLabel')}</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  required autoComplete="current-password" placeholder={t('auth.passwordPlaceholder')}
                  className="w-full px-4 py-3 pr-11 rounded-xl bg-white text-gray-900 text-sm placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400 transition-all"
                />
                <button type="button" onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                  {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>

            {/* Forgot */}
            <div className="flex justify-end -mt-1">
              <button type="button" onClick={() => setForgotOpen(true)} className="text-xs font-semibold text-gray-700 hover:text-brand-600 transition-colors">
                {t('auth.forgotPassword')}
              </button>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading}
              className="w-full py-3 mt-1 rounded-xl font-bold text-sm text-white bg-brand-400 hover:bg-brand-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  {t('auth.loggingIn')}
                </>
              ) : t('auth.login')}
            </button>
          </form>
        </div>
      </div>

      {forgotOpen && (
        <ForgotPasswordModal initialEmail={email} onClose={() => setForgotOpen(false)} />
      )}
    </div>
  )
}

// ── Forgot password: request an OTP by email, then reset with OTP + new password ──
function ForgotPasswordModal({ initialEmail, onClose }: { initialEmail: string; onClose: () => void }) {
  const { t } = useTranslation()
  const [step, setStep] = useState<'email' | 'reset' | 'done'>('email')
  const [email, setEmail] = useState(initialEmail)
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const sendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) { setError(t('auth.forgot.emailRequired')); return }
    setError(''); setLoading(true)
    try {
      await authService.forgotPassword(email.trim())
      setStep('reset')
    } catch (err) {
      setError(getApiError(err))
    } finally { setLoading(false) }
  }

  const resetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otp.trim()) { setError(t('auth.forgot.otpRequired')); return }
    if (password.length < 6) { setError(t('auth.forgot.passwordTooShort')); return }
    if (password !== confirm) { setError(t('auth.forgot.passwordMismatch')); return }
    setError(''); setLoading(true)
    try {
      await authService.verifyOTP({ email: email.trim(), otp: otp.trim(), password })
      setStep('done')
    } catch (err) {
      setError(getApiError(err))
    } finally { setLoading(false) }
  }

  const inCls = 'w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <button onClick={onClose} className="absolute top-4 end-4 text-gray-400 hover:text-gray-600"><X size={18} /></button>

        {step === 'done' ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={26} className="text-green-500" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">{t('auth.forgot.resetTitle')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('auth.forgot.resetDesc')}</p>
            <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold transition-colors">{t('auth.forgot.backToLogin')}</button>
          </div>
        ) : (
          <>
            {step === 'reset' && (
              <button onClick={() => { setStep('email'); setError('') }} className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-gray-600 mb-3">
                <ArrowLeft size={13} /> {t('auth.forgot.changeEmail')}
              </button>
            )}
            <h2 className="text-lg font-bold text-gray-900 mb-1">{t('auth.forgot.title')}</h2>
            <p className="text-sm text-gray-500 mb-4">
              {step === 'email'
                ? t('auth.forgot.descEmailStep')
                : t('auth.forgot.descResetStep', { email })}
            </p>

            {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-600 mb-3 whitespace-pre-line">{error}</div>}

            {step === 'email' ? (
              <form onSubmit={sendOtp} className="space-y-3">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('auth.forgot.emailPlaceholder')} className={inCls} autoFocus />
                <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold transition-colors disabled:opacity-60">
                  {loading ? t('auth.forgot.sending') : t('auth.forgot.sendOtp')}
                </button>
              </form>
            ) : (
              <form onSubmit={resetPassword} className="space-y-3">
                <input value={otp} onChange={e => setOtp(e.target.value)} placeholder={t('auth.forgot.otpPlaceholder')} className={inCls} autoFocus />
                <div className="relative">
                  <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder={t('auth.forgot.newPasswordPlaceholder')} className={inCls + ' pe-10'} />
                  <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t('auth.forgot.confirmPlaceholder')} className={inCls} />
                <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold transition-colors disabled:opacity-60">
                  {loading ? t('auth.forgot.resetting') : t('auth.forgot.resetPassword')}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  )
}
